import { ApolloError, MutationTuple, useMutation, useQuery } from '@apollo/client';
import {
  ButtonVariant,
  Device,
  ResponsiveTypography,
  ResponsiveTypographyVariant,
  useDeviceViewport,
} from '@se-admin/falcon';
import { i18n } from '@secret-escapes/i18n';
import { History } from 'history';
import * as moment from 'moment';
import * as React from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { useHistory, useParams } from 'react-router-dom';
import { TranslationsContext } from '../../common/contexts';
import { ApplicationContext } from '../../common/contexts/ApplicationContext';
import { IDates, IPageName } from '../../common/types/apiSchema';
import { getNestedValue } from '../../common/utils/parsingUtils';
import { ErrorKey, ErrorType } from '../../common/utils/tracking/tracking.interfaces';
import { trackError } from '../../common/utils/tracking/trackingUtils';
import { FeatureFlags } from '../../constants/featureFlags';
import { getPaths } from '../../constants/paths/paths';
import { useFeatureFlag } from '../../utils/FeatureFlagUtils';
import BookingAmendments, { DEFAULT_TRAVEL_PERIOD } from '../BookingAmendments';
import { RESET_TRAVEL_PERIOD } from '../BookingAmendments/BookingAmendments.mutations';
import {
  GET_FILTERS_DATA,
  ITravelPeriodData,
} from '../BookingAmendments/BookingAmendments.queries';
import CancellationSummary from '../CancellationSummary';
import {
  ICancellationSummary,
  ICancellationSummaryProps,
  ICancellationSummaryQueryData,
  ICancellationSummaryQueryVariables,
} from '../CancellationSummary/CancellationSummary.interfaces';
import { StyledButton } from '../CancellationSummary/CancellationSummary.styles';
import CancellationComplete from './CancellationComplete';
import { IMutationProps } from './CancellationPage.interfaces';
import { CANCEL_BOOKING, GET_BOOKING_DATA } from './CancellationPage.queries';
import {
  CancellationPageContainer,
  CancellationPageOuterContainer,
  DisclaimerText,
  ReturnButtonContainer,
} from './CancellationPage.styles';

const DEFAULT_REFUND_DURATION: number = 14;
const DEFAULT_CREDIT_PERCENT: number = 0;
/**
 * Cancellation Page
 */
const CancellationPage: React.FunctionComponent<{}> = () => {
  const { translations } = React.useContext(TranslationsContext);
  const { bookingId = null } = useParams<{ bookingId: string }>();
  const [creditRefund, setCreditRefund] = React.useState<boolean>(true);
  const [cancellationError, setCancellationError] = React.useState<boolean>(false);
  const [cancellationComplete, setCancellationComplete] = React.useState<boolean>(false);
  const [cancellationRequested, setCancellationRequested] = React.useState<boolean>(false);
  const history: History = useHistory();
  const bookingsPageUrl: string = getPaths(IPageName.bookingsPage)[0];
  const cancellationRebookingFlag: boolean = useFeatureFlag(FeatureFlags.cancellationRebooking);
  const cancellationAmendmentsFlag: boolean = useFeatureFlag(FeatureFlags.cancellationAmendments);
  const deviceViewport: Device = useDeviceViewport();
  const isMobile: boolean = deviceViewport === Device.mobile;

  const query: URLSearchParams = new URLSearchParams(history.location.search);
  const rebooking: boolean = query.has('rebook');
  /**
   * Without amendments enabled rebooking flow is visually same as cancellation flow
   */
  const amending: boolean = rebooking && cancellationAmendmentsFlag;

  const setErroneousState: () => void = () => {
    setCancellationError(true);
    setCancellationComplete(true);
    setCancellationRequested(false);
  };

  /**
   * Error handling for no booking id given (must be optional at application level)
   */
  if (!bookingId && !cancellationError) {
    setErroneousState();
  }

  const { loading, error, data } = useQuery<
    ICancellationSummaryQueryData,
    ICancellationSummaryQueryVariables
  >(GET_BOOKING_DATA, {
    context: { protectedFlashsales: true },
    variables: {
      bookingId,
      refundMethod: creditRefund ? 'CREDIT_NO_EXTRA' : null,
    },
  });

  if (error && !cancellationError) {
    trackError(ErrorKey.CANCELLATION_SUMMARY_LOAD, ErrorType.LOADING_ERROR, error.message, error);
    setErroneousState();
  }

  const cancellationSummary: ICancellationSummary = getNestedValue(
    ['booking', 'cancellationSummary'],
    data
  );

  const { affiliateCode, currency } = React.useContext(ApplicationContext);

  const offerId: string = getNestedValue(['booking', 'offerId'], data);

  const saleId: string = getNestedValue(['booking', 'saleId'], data);

  const rooms: number = getNestedValue(['booking', 'rooms'], data);

  const adults: number = getNestedValue(['booking', 'occupancy', 'adults'], data);

  const bookingDates: IDates = getNestedValue(['booking', 'bookingDates'], data);

  const onMutationCompleted: (response: any) => void = response => {
    if (response.cancelBooking.success) {
      setCancellationComplete(true);
    } else {
      setErroneousState();
    }
  };

  const onMutationError: (response: ApolloError) => void = response => {
    trackError(
      ErrorKey.CANCELLATION_CONFIRMATION,
      ErrorType.MUTATION_ERROR,
      response.message,
      response
    );
    setErroneousState();
  };

  const cancelBookingMutationProps: IMutationProps = {
    mutation: CANCEL_BOOKING,
    onCompleted: onMutationCompleted,
    onError: onMutationError,
  };

  const [cancelBooking] = useMutation(
    cancelBookingMutationProps.mutation,
    cancelBookingMutationProps
  );

  const handleConfirm: () => void = () => {
    if (!cancellationRequested) {
      cancelBooking({
        context: { protectedFlashsales: true },
        variables: {
          bookingId,
          rebooking: cancellationRebookingFlag ? rebooking : undefined,
          refundMethod: creditRefund ? 'CREDIT_NO_EXTRA' : null,
        },
      });
      setCancellationRequested(true);
    }
  };

  const handleToggleCreditRefund: () => void = () => {
    setCreditRefund(!creditRefund);
  };

  const navigateToBookingPage: () => void = () => {
    history.push(`${bookingsPageUrl}`);
  };

  const navigateAndRefreshToBookingPage: () => void = () => {
    window.location.assign(getPaths(IPageName.bookingsPage)[0]);
  };

  const summaryProps: ICancellationSummaryProps = {
    cancellationRequested,
    cancellationSummary,
    creditPercent: DEFAULT_CREDIT_PERCENT,
    creditRefund,
    handleCancel: navigateToBookingPage,
    handleConfirm,
    handleToggleCreditRefund,
    refundDuration: DEFAULT_REFUND_DURATION,
  };

  const { data: filtersData } = useQuery<ITravelPeriodData>(GET_FILTERS_DATA);
  const { travelPeriod, travelPeriodDraft } = filtersData
    ? filtersData
    : {
        ...DEFAULT_TRAVEL_PERIOD,
      };

  const [resetTravelPeriod]: MutationTuple<{}, {}> = useMutation(RESET_TRAVEL_PERIOD);

  const initialiseFilters: () => void = () => {
    resetTravelPeriod();
  };

  React.useMemo(() => {
    window.scrollTo(0, 0);
    initialiseFilters();
  }, []);

  return (
    <RemoveScroll enabled={amending && isMobile}>
      <CancellationPageOuterContainer data-testid="cancellationPage" addMobilePadding={!amending}>
        {!loading && (
          <CancellationPageContainer>
            {!cancellationSummary || cancellationError ? (
              <React.Fragment>
                <ResponsiveTypography variant={ResponsiveTypographyVariant.t5SansBold}>
                  {i18n('cancellation.generic.error', [], translations)}
                </ResponsiveTypography>
                <DisclaimerText variant={ResponsiveTypographyVariant.body}>
                  {i18n('cancellation.request.error', [], translations)}
                </DisclaimerText>
                <ReturnButtonContainer>
                  <StyledButton
                    variant={ButtonVariant.primary}
                    onClick={navigateAndRefreshToBookingPage}
                  >
                    {i18n('cancellation.back.to.bookings', [], translations)}
                  </StyledButton>
                </ReturnButtonContainer>
              </React.Fragment>
            ) : amending ? (
              <BookingAmendments
                saleId={saleId}
                offerId={offerId}
                rooms={rooms}
                currency={currency}
                affiliate={affiliateCode}
                adults={adults}
                originalStartDate={moment(bookingDates.start, 'YYYY-MM-DD')}
                originalEndDate={moment(bookingDates.end, 'YYYY-MM-DD')}
                travelPeriod={travelPeriod}
                travelPeriodDraft={travelPeriodDraft}
              />
            ) : cancellationComplete ? (
              <CancellationComplete summaryProps={summaryProps} />
            ) : (
              <CancellationSummary {...summaryProps} />
            )}
          </CancellationPageContainer>
        )}
      </CancellationPageOuterContainer>
    </RemoveScroll>
  );
};

export default CancellationPage;
